/**
 * New Year coloring pages — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_seasons`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const newYearColoringPagesPack: ToilePack = {
    slug: 'new-year-coloring-pages',
    category: 'season',
    name: 'New Year coloring pages',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'fireworks', label: 'Fireworks', subject: 'Fireworks bursting over a row of city buildings, stars, a moon'},
        {key: 'greeting', label: 'Lettered greeting', subject: 'The words "HAPPY NEW YEAR" spelled exactly in big bubble letters filled with patterns to colour, confetti around'},
        {key: 'party', label: 'Party table', subject: 'A party table with balloons, a cake, glasses and streamers, a clock on the wall'},
    ],
    styles: [
        {key: 'simple', label: 'Simple lines', style: 'bold single-weight black outline drawing, large open shapes, clean closed line art', palette: 'Black ink only', framing: 'Centered on a pure white page with a white margin, the full page used'},
        {key: 'detailed', label: 'Detailed', style: 'intricate fine black line art, dense decorative detail, clean closed line art', palette: 'Black ink only', framing: 'Filling the page edge to edge inside a thin white margin'},
        {key: 'doodle', label: 'Doodle', style: 'hand-drawn marker doodle line art, loose confident strokes, clean closed line art', palette: 'Black ink only', framing: 'Scattered across the page with a white margin'},
    ],
    directions: ['a clock at midnight', 'with a countdown banner', 'for a family activity page'],
    directionHint: 'try: a clock at midnight',
    seo: {
        title: 'New Year coloring pages: fireworks, cheers, party',
        metaDescription: 'Nine New Year coloring pages — fireworks, a HAPPY NEW YEAR greeting, a party table — in simple, detailed or doodle line art.',
        h1: 'New Year coloring pages: fireworks, greeting and party table',
        tagline: 'Nine coloring pages, three midnight scenes, three line styles each — clean outlines ready to fill in.',
        intro: 'A New Year coloring page for midnight: fireworks bursting over a city skyline, the words HAPPY NEW YEAR in big bubble letters patterned to color, a party table of balloons and streamers. Each is drawn in bold simple lines, intricate detailed line art, or loose hand-drawn doodle strokes. Flat black outlines, no shading, ready to color. Ask for a different scene, like a clock at midnight, and it redraws.',
        useCases: [
            "a New Year's Eve coloring page for kids",
            'a classroom countdown activity',
            'a party-table activity for the countdown',
            'a family activity page for midnight',
        ],
        faq: [
            {q: 'What size do these print at?', a: 'Each page is drawn at portrait A4 proportions with a white margin, so it prints clean on standard paper.'},
            {q: 'Is the lettering readable once colored?', a: 'Yes — the HAPPY NEW YEAR greeting is drawn in large bubble letters with room inside each one to color.'},
            {q: 'Can I get a different New Year scene?', a: "Yes — that's what the direction field is for. Type a different scene, like a clock at midnight, and it redraws in the same three line styles."},
        ],
    },
}
